/**
 * Configurações de código postal por country code (ISO 3166-1 alpha-2)
 *
 * Países suportados:
 * - BR: CEP brasileiro - formato #####-###
 * - US: ZIP Code americano - formato ##### ou #####-####
 * - AR: Código Postal argentino - formato #### ou A####AAA
 * - CO: Código Postal colombiano - formato ######
 * - MX: Código Postal mexicano - formato #####
 * - PE: Código Postal peruano - formato #####
 * - ES: Código Postal espanhol - formato #####
 *
 * Nota: O label do código postal deve ser obtido via i18n usando a chave
 * 'c:components.AddressFieldset.labels.postalCode'
 */
const postalCodeConfigs: PostalCodeConfigs = {
  // Brasil
  BR: {
    format: '#####-###',
    placeholder: '12345-678',
    pattern: /^\d{5}-?\d{3}$/,
    length: 8,
    normalize: (value: string) => value.replace(/\D/g, ''),
    validate: (value: string) => {
      const normalized = value.replace(/\D/g, '')
      return normalized.length === 8
    }
  },

  // Estados Unidos
  US: {
    format: '#####',
    placeholder: '12345',
    pattern: /^\d{5}(-\d{4})?$/,
    length: 5,
    normalize: (value: string) => value.replace(/[^\d-]/g, ''),
    validate: (value: string) => {
      const normalized = value.replace(/[^\d-]/g, '')
      return /^\d{5}(-\d{4})?$/.test(normalized)
    }
  },

  // Argentina
  AR: {
    format: '####',
    placeholder: '1234',
    pattern: /^[A-Z]?\d{4}[A-Z]{0,3}$/i,
    length: 4,
    normalize: (value: string) => value.replace(/[^a-zA-Z0-9]/g, ''),
    validate: (value: string) => {
      const normalized = value.replace(/[^a-zA-Z0-9]/g, '')
      // Argentina aceita formato antigo (4 dígitos) ou CPA (A1234AAA)
      return (
        /^\d{4}$/.test(normalized) || /^[A-Z]\d{4}[A-Z]{3}$/i.test(normalized)
      )
    }
  },

  // Colômbia
  CO: {
    format: '######',
    placeholder: '123456',
    pattern: /^\d{6}$/,
    length: 6,
    normalize: (value: string) => value.replace(/\D/g, ''),
    validate: (value: string) => {
      const normalized = value.replace(/\D/g, '')
      return normalized.length === 6
    }
  },

  // México
  MX: {
    format: '#####',
    placeholder: '12345',
    pattern: /^\d{5}$/,
    length: 5,
    normalize: (value: string) => value.replace(/\D/g, ''),
    validate: (value: string) => {
      const normalized = value.replace(/\D/g, '')
      return normalized.length === 5
    }
  },

  // Peru
  PE: {
    format: '#####',
    placeholder: '12345',
    pattern: /^\d{5}$/,
    length: 5,
    normalize: (value: string) => value.replace(/\D/g, ''),
    validate: (value: string) => {
      const normalized = value.replace(/\D/g, '')
      return normalized.length === 5
    }
  },

  // Espanha
  ES: {
    format: '#####',
    placeholder: '12345',
    pattern: /^\d{5}$/,
    length: 5,
    normalize: (value: string) => value.replace(/\D/g, ''),
    validate: (value: string) => {
      const normalized = value.replace(/\D/g, '')
      return normalized.length === 5
    }
  }
}

// Configuração padrão (Brasil)
const defaultConfig = postalCodeConfigs.BR

/**
 * Obtém a configuração de código postal baseada no country code (ISO 3166-1 alpha-2)
 *
 * @param countryCode - Código do país (ex: 'BR', 'US', 'AR', 'CO', 'MX', 'PE', 'ES')
 * @returns Configuração do código postal para o país especificado
 *
 * @example
 * const config = getPostalCodeConfig('BR')
 * console.log(config.format) // '#####-###'
 *
 * const config = getPostalCodeConfig('US')
 * console.log(config.format) // '#####'
 */
export const getPostalCodeConfig = (countryCode?: string): PostalCodeConfig => {
  if (!countryCode) {
    return defaultConfig
  }

  // Normaliza o country code para uppercase para evitar problemas de case sensitivity
  const normalized = countryCode.toUpperCase().trim()

  // Tenta encontrar configuração exata
  if (postalCodeConfigs[normalized]) {
    return postalCodeConfigs[normalized]
  }

  // Se não encontrar, retorna a configuração padrão
  return defaultConfig
}

/**
 * Valida um código postal baseado no country code
 *
 * @param value - Valor do código postal
 * @param countryCode - Código do país (ISO 3166-1 alpha-2)
 * @returns true se o código postal é válido
 */
export const validatePostalCode = (
  value: string,
  countryCode?: string
): boolean => {
  const config = getPostalCodeConfig(countryCode)
  return config.validate(value)
}

/**
 * Normaliza um código postal removendo caracteres inválidos
 *
 * @param value - Valor do código postal
 * @param countryCode - Código do país (ISO 3166-1 alpha-2)
 * @returns Código postal normalizado
 */
export const normalizePostalCode = (
  value: string,
  countryCode?: string
): string => {
  const config = getPostalCodeConfig(countryCode)
  return config.normalize(value)
}

export default getPostalCodeConfig
